import React, { type FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { AvatarRow } from './AvatarRow.tsx';

const i18n = defineMessages({
	cancelApprovalButtonLabel: {
		id: 'approvals.cancel-approval.modal.cancel-approval.button.label',
		defaultMessage: 'Cancel approval',
		description: 'Label for a button to confirm canceling the approval',
	},
	modalHeading: {
		id: 'approvals.cancel-approval.modal.heading',
		defaultMessage: 'Cancel approval?',
		description:
			'The text is shown as the heading at the top of the cancel approval confirmation modal dialog, prompting the user to confirm they want to cancel the ongoing approval.',
	},
	modalDescription: {
		id: 'approvals.cancel-approval.modal.description',
		defaultMessage:
			'Current assigned approvers will no longer be able to approve or request changes. You can still collaborate or start another approval at a later date.',
		description: 'Description explaining the consequences of canceling approval',
	},
	approverMessage: {
		id: 'approvals.cancel-approval.modal.single-user.message',
		defaultMessage: '{name} already responded',
		description: 'Message showing the only user who responded',
	},
	approversAndCountMessage: {
		id: 'approvals.cancel-approval.modal.multiple-users.message',
		defaultMessage: '{name} and {count, plural, one {# other} other {# others}} already responded',
		description: 'Message showing the first user who responded and the count of other responders',
	},
});

const styles = cssMap({
	footerRow: {
		justifyContent: 'flex-end',
		gap: token('space.150'),
		marginTop: token('space.200'),
	},
});

type CancelApprovalModalFooterOptionsProps = {
	onCancelApproval: () => void;
};

const CancelApprovalModalFooterOptions: FC<CancelApprovalModalFooterOptionsProps> = ({
	onCancelApproval,
}) => {
	return (
		<Inline xcss={styles.footerRow}>
			<Button
				appearance="warning"
				onClick={onCancelApproval}
				testId="cancel-approval-modal-confirm-button"
			>
				<FormattedMessage {...i18n.cancelApprovalButtonLabel} />
			</Button>
		</Inline>
	);
};

type CancelApprovalModalProps = {
	approverData: Array<{ name: string; src?: string; email?: string; userId?: string }>;
	onCancelApproval: () => void;
	onClose: () => void;
	showProfileCards?: boolean;
};

export const CancelApprovalModal: FC<CancelApprovalModalProps> = ({
	approverData,
	onCancelApproval,
	onClose,
	showProfileCards,
}) => {
	const { Modal } = useChoreographerAssets();

	return (
		<ModalTransition>
			<Modal onClose={onClose}>
				<ModalHeader hasCloseButton>
					<ModalTitle appearance="warning">
						<FormattedMessage {...i18n.modalHeading} />
					</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<Stack>
						<AvatarRow
							approverData={approverData}
							messages={{
								singleUserMessage: i18n.approverMessage,
								multipleUsersMessage: i18n.approversAndCountMessage,
							}}
							maxCount={2}
							showProfileCards={showProfileCards}
						/>
						<FormattedMessage {...i18n.modalDescription} />
					</Stack>
				</ModalBody>
				<ModalFooter>
					<CancelApprovalModalFooterOptions onCancelApproval={onCancelApproval} />
				</ModalFooter>
			</Modal>
		</ModalTransition>
	);
};
